import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Plus, MagnifyingGlass, Users, PencilSimple, Archive, LinkSimple } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  listCooperators,
  createCooperator,
  updateCooperator,
  archiveCooperator,
  linkCooperatorPortalUser,
  listAvailablePortalUsers,
  COLLEGES,
  COOPERATOR_TYPES,
  COOPERATOR_STATUSES,
} from '../../services/cmcApi';
import { CmcPageHeader, CmcStatusBadge, CmcEmpty, fmtDateOnly } from '../../components/cmc/CmcCommon';
import { formatApiError } from '../../utils/apiError';
import { Checkbox } from '../../components/ui/checkbox';
import { useBatchSelection } from '../../hooks/useBatchSelection';
import { batchSetCooperatorsStatus } from '../../services/api';

const emptyForm = {
  type: 'personne_physique',
  first_name: '',
  last_name: '',
  legal_name: '',
  email: '',
  phone: '',
  territory: '',
  college: 'E_DEVELOPPEURS_CONTRIBUTEURS',
  status: 'active',
};

export default function CmcCooperatorsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCollege, setFilterCollege] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [linkDialog, setLinkDialog] = useState(null); // cooperator to link
  const batch = useBatchSelection();
  const [batchBusy, setBatchBusy] = useState(false);

  const doBatchStatus = async (newStatus) => {
    if (!batch.hasSelection) return;
    setBatchBusy(true);
    try {
      const r = await batchSetCooperatorsStatus(Array.from(batch.selectedIds), newStatus);
      toast.success(`${r.data.updated.length} coopérateur(s) → ${newStatus}, ${r.data.skipped.length} ignoré(s)`);
      batch.clear();
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur batch'); }
    setBatchBusy(false);
  };
  const [portalUsers, setPortalUsers] = useState([]);
  const [selectedPortalUser, setSelectedPortalUser] = useState('');

  const openLinkDialog = async (coop) => {
    setLinkDialog(coop);
    setSelectedPortalUser(coop.portal_user_id || '');
    try {
      const res = await listAvailablePortalUsers();
      setPortalUsers(res.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitLink = async () => {
    try {
      await linkCooperatorPortalUser(linkDialog.id, selectedPortalUser || null);
      toast.success(selectedPortalUser ? 'Utilisateur portail rattaché' : 'Rattachement supprimé');
      setLinkDialog(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCollege !== 'all') params.college = filterCollege;
      const res = await listCooperators(params);
      setItems(res.data.items || []);
    } catch {
      toast.error('Erreur de chargement des coopérateurs');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterCollege]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      type: c.type || 'personne_physique',
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      legal_name: c.legal_name || '',
      email: c.email || '',
      phone: c.phone || '',
      territory: c.territory || '',
      college: c.college || 'E_DEVELOPPEURS_CONTRIBUTEURS',
      status: c.status || 'active',
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.college) { toast.error('Le collège est requis'); return; }
    if (form.type === 'personne_physique' && !form.last_name) {
      toast.error('Le nom est requis'); return;
    }
    if (form.type === 'personne_morale' && !form.legal_name) {
      toast.error('La raison sociale est requise'); return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateCooperator(editing.id, form);
        toast.success('Coopérateur mis à jour');
      } else {
        await createCooperator(form);
        toast.success('Coopérateur créé');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const onArchive = async (c) => {
    if (!window.confirm(`Archiver le coopérateur ${c.first_name || c.legal_name || ''} ?`)) return;
    try {
      await archiveCooperator(c.id);
      toast.success('Coopérateur archivé');
      fetchData();
    } catch {
      toast.error("Impossible d'archiver");
    }
  };

  return (
    <div data-testid="cmc-cooperators-page">
      <CmcPageHeader
        title="Coopérateurs-contributeurs"
        subtitle="Registre CMC : personnes physiques et morales affiliées"
        actions={
          <Button
            onClick={openCreate}
            className="bg-[#2D5A4C] hover:bg-[#1E4136] text-white"
            data-testid="coop-create-btn"
          >
            <Plus size={16} className="mr-2" /> Nouveau coopérateur
          </Button>
        }
      />

      {batch.hasSelection && (
        <Card className="border-[#2D5A4C] bg-[#F0F8E4] mb-4" data-testid="coop-batch-toolbar">
          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
            <p className="text-sm font-bold text-[#2D5A4C]">{batch.count} sélectionné(s)</p>
            <div className="flex-1"></div>
            <Button size="sm" onClick={() => doBatchStatus('active')} disabled={batchBusy} className="bg-[#80C51F] hover:bg-[#6ba317] text-white" data-testid="coop-batch-activate">
              {batchBusy && <Loader2 size={12} className="animate-spin mr-1" />} Activer
            </Button>
            <Button size="sm" onClick={() => doBatchStatus('suspended')} disabled={batchBusy} className="bg-[#FF8E00] hover:bg-[#e07d00] text-white" data-testid="coop-batch-suspend">
              Suspendre
            </Button>
            <Button size="sm" onClick={() => doBatchStatus('archived')} disabled={batchBusy} className="bg-[#5E6D66] hover:bg-[#4a5750] text-white" data-testid="coop-batch-archive">
              <Archive size={12} className="mr-1" /> Archiver
            </Button>
            <Button size="sm" variant="ghost" onClick={batch.clear} data-testid="coop-batch-clear">Effacer</Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#E8E6E1] mb-4">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
            <Input
              data-testid="coop-search-input"
              placeholder="Rechercher (nom, email, n° associé)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]" data-testid="coop-filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {COOPERATOR_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterCollege} onValueChange={setFilterCollege}>
            <SelectTrigger className="w-[260px]" data-testid="coop-filter-college"><SelectValue placeholder="Collège" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous collèges</SelectItem>
              {COLLEGES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
          ) : items.length === 0 ? (
            <CmcEmpty icon={Users} message="Aucun coopérateur enregistré" hint="Créez le premier coopérateur pour démarrer" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F8F5] text-[11px] uppercase tracking-wide text-[#5E6D66]">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-medium w-8">
                      <Checkbox
                        checked={items.length > 0 && items.every((c) => batch.isSelected(c.id))}
                        onCheckedChange={(v) => (v ? batch.selectAll(items) : batch.clear())}
                        data-testid="coop-batch-select-all"
                      />
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium">N° associé</th>
                    <th className="text-left px-4 py-2.5 font-medium">Nom / Raison sociale</th>
                    <th className="text-left px-4 py-2.5 font-medium">Type</th>
                    <th className="text-left px-4 py-2.5 font-medium">Collège</th>
                    <th className="text-left px-4 py-2.5 font-medium">Statut</th>
                    <th className="text-left px-4 py-2.5 font-medium">Créé</th>
                    <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0EFEB]">
                  {items.map((c) => (
                    <tr key={c.id} className={`hover:bg-[#FAFAF8] ${batch.isSelected(c.id) ? 'bg-[#F0F8E4]' : ''}`} data-testid={`coop-row-${c.id}`}>
                      <td className="px-3 py-3">
                        <Checkbox
                          checked={batch.isSelected(c.id)}
                          onCheckedChange={() => batch.toggle(c.id)}
                          data-testid={`coop-select-${c.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-[#1A2421]">{c.numero_associe}</td>
                      <td className="px-4 py-3 text-[#1A2421] font-medium">
                        {c.type === 'personne_morale'
                          ? c.legal_name || '-'
                          : `${c.first_name || ''} ${c.last_name || ''}`.trim() || '-'}
                        {c.email && <div className="text-[11px] text-[#5E6D66] font-normal">{c.email}</div>}
                        {c.portal_user_id && (
                          <div className="text-[10px] text-blue-700 font-medium mt-0.5 flex items-center gap-1">
                            <LinkSimple size={9} /> Portail: {c.portal_user_email || 'lié'}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66] capitalize">
                        {c.type === 'personne_morale' ? 'Pers. morale' : 'Pers. physique'}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">
                        {(COLLEGES.find((co) => co.value === c.college)?.label) || c.college || '-'}
                      </td>
                      <td className="px-4 py-3"><CmcStatusBadge status={c.status} label={COOPERATOR_STATUSES.find((s) => s.value === c.status)?.label} /></td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">{fmtDateOnly(c.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`coop-edit-${c.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openLinkDialog(c)} data-testid={`coop-link-portal-${c.id}`} title="Rattacher au portail">
                          <LinkSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onArchive(c)} data-testid={`coop-archive-${c.id}`} className="text-red-600">
                          <Archive size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="coop-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le coopérateur' : 'Nouveau coopérateur'}</DialogTitle>
            <DialogDescription>Renseignez les informations du coopérateur-contributeur CMC.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="coop-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{COOPERATOR_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Collège *</Label>
              <Select value={form.college} onValueChange={(v) => setForm({ ...form, college: v })}>
                <SelectTrigger data-testid="coop-form-college"><SelectValue /></SelectTrigger>
                <SelectContent>{COLLEGES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.type === 'personne_physique' ? (
              <>
                <div>
                  <Label>Prénom</Label>
                  <Input data-testid="coop-form-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                </div>
                <div>
                  <Label>Nom *</Label>
                  <Input data-testid="coop-form-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                </div>
              </>
            ) : (
              <div className="md:col-span-2">
                <Label>Raison sociale *</Label>
                <Input data-testid="coop-form-legal-name" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
              </div>
            )}
            <div>
              <Label>Email</Label>
              <Input type="email" data-testid="coop-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input data-testid="coop-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>Territoire</Label>
              <Input data-testid="coop-form-territory" value={form.territory} onChange={(e) => setForm({ ...form, territory: e.target.value })} placeholder="ex: Guadeloupe" />
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="coop-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{COOPERATOR_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="coop-cancel-btn">Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="coop-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog rattachement portail */}
      <Dialog open={!!linkDialog} onOpenChange={(o) => !o && setLinkDialog(null)}>
        <DialogContent className="max-w-md" data-testid="link-portal-dialog">
          <DialogHeader>
            <DialogTitle>Rattacher au portail coopérateur</DialogTitle>
            <DialogDescription>
              {linkDialog?.first_name} {linkDialog?.last_name} {linkDialog?.legal_name} · Réf. {linkDialog?.numero_associe}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Utilisateur portail (membre externe)</Label>
              <Select value={selectedPortalUser || 'none'} onValueChange={(v) => setSelectedPortalUser(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="link-portal-user-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Aucun (délier) —</SelectItem>
                  {portalUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.email} {u.name ? `(${u.name})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {portalUsers.length === 0 && (
                <p className="text-[10px] text-[#5E6D66] mt-1">Aucun utilisateur membre externe. Créez-en un dans la page Utilisateurs.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog(null)}>Annuler</Button>
            <Button onClick={submitLink} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="link-portal-save-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
